import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthAPIHandler } from "../../services/NetworkClient/BackendAPIs/AuthAPIHandler";
import { SessionManager } from "../../services/SessionManager/SessionManager";
import { ServerConfig } from "../../services/NetworkClient/ServerConfig";
import { validateEmail, validatePassword } from "../../utils/validators";
import { store } from "../../redux/store";
import { login as loginAction } from "../../redux/auth/authSlice";
import logAnalyticsEvent from "../../services/AnalyticsManager/AnalyticsManager";
import { GGAnalyticsEvents } from "../../services/AnalyticsManager/GGAnalyticsEvents";

export const useAuthPage = () => {
    const navigate = useNavigate();

    // Form state
    let [name, setName] = useState("");
    let [loginEmail, setLoginEmail] = useState("");
    let [registerEmail, setRegisterEmail] = useState("");
    let [loginPassword, setLoginPassword] = useState("");
    let [registerPassword, setRegisterPassword] = useState("");
    let [confirmPassword, setConfirmPassword] = useState("");
    let [loginErrorMessage, setLoginErrorMessage] = useState("");
    let [registerErrorMessage, setRegisterErrorMessage] = useState("");
    let [loginActive, setLoginActive] = useState(true);

    // Async request state (drives loader buttons)
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);

    // Bot traps — only automated form fillers ever populate these
    const [loginHoneypot, setLoginHoneypot] = useState("");
    const [registerHoneypot, setRegisterHoneypot] = useState("");

    // Password visibility state
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Modal state
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [showEmailOTPVerificationModal, setShowEmailOTPVerificationModal] = useState(false);

    const loggedInStatus = SessionManager.shared.isLoggedin();

    useEffect(() => {
        document.title = "GenieGig | " + (loginActive ? "Login" : "Register");
        logAnalyticsEvent(GGAnalyticsEvents.AUTH_PAGE_LOADED);
    }, [loginActive]);

    // --- UI transitions ---

    const switchToSignIn = () => {
        const container = document.getElementById('container');
        container!.classList.remove("right-panel-active");
    };

    const switchToSignUp = () => {
        const container = document.getElementById('container');
        container!.classList.add("right-panel-active");
    };

    // --- Password visibility toggles ---

    const toggleLoginPasswordVisibility = () => setShowLoginPassword(!showLoginPassword);
    const toggleRegisterPasswordVisibility = () => setShowRegisterPassword(!showRegisterPassword);
    const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

    // --- Auth actions ---

    const handleLogin = async () => {
        setLoginErrorMessage("");

        const emailValidation = validateEmail(loginEmail);
        if (!emailValidation.status) {
            setLoginErrorMessage(emailValidation.error);
            return;
        }
        const passwordValidation = validatePassword(loginPassword);
        if (!passwordValidation.status) {
            setLoginErrorMessage(passwordValidation.error);
            return;
        }
        
        // Fallback if legacy accounts with weak passwords report being locked out:
        // swap the strength check above for this presence-only check.
        // if (loginPassword.length === 0) {
        //     setLoginErrorMessage("Please enter your password");
        //     return;
        // }

        try {
            setIsLoggingIn(true);
            const buyer = await AuthAPIHandler.shared.login(loginEmail, loginPassword, loginHoneypot);
            store.dispatch(loginAction(buyer));
            navigate("/");
        } catch (error: any) {
            setLoginErrorMessage(error);
        } finally {
            setIsLoggingIn(false);
        }
    };

    const validateRegistrationForm = async () => {
        setRegisterErrorMessage("");

        if (name.trim() === "") {
            setRegisterErrorMessage("Name cannot be empty");
            return;
        }
        const emailValidation = validateEmail(registerEmail);
        if (!emailValidation.status) {
            setRegisterErrorMessage(emailValidation.error);
            return;
        }
        const passwordValidation = validatePassword(registerPassword);
        if (!passwordValidation.status) {
            setRegisterErrorMessage(passwordValidation.error);
            return;
        }
        if (registerPassword !== confirmPassword) {
            setRegisterErrorMessage("Passwords do not match");
            return;
        }

        try {
            setIsSubmittingRegistration(true);
            await AuthAPIHandler.shared.requestOtpForEmailVerification(registerEmail, name, registerHoneypot);
            setShowEmailOTPVerificationModal(true);
        } catch (error: any) {
            setRegisterErrorMessage(error);
        } finally {
            setIsSubmittingRegistration(false);
        }
    };

    const handleGoogleAuth = () => {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
            'about:blank',
            'google-auth-popup',
            `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
            window.location.replace(AuthAPIHandler.shared.getGoogleAuthPageURL());
            return;
        }

        popup.location.href = AuthAPIHandler.shared.getGoogleAuthPageURL();

        const handleMessage = (event: MessageEvent) => {
            const allowedOrigin = ServerConfig.shared.baseDomainName.replace(/\/$/, '');
            if (event.origin !== allowedOrigin) return;

            if (event.data?.type === 'google-auth-success') {
                window.removeEventListener('message', handleMessage);
                try {
                    const buyer = AuthAPIHandler.shared.performPostLoginFlow(event.data.payload);
                    if (buyer) {
                        navigate("/");
                    }
                } catch (err) {
                    setLoginErrorMessage("Google sign-in failed. Please try again.");
                    setRegisterErrorMessage("Google sign-in failed. Please try again.");
                }
            }
        };

        window.addEventListener('message', handleMessage);

        const checkPopupClosed = setInterval(() => {
            if (popup.closed) {
                clearInterval(checkPopupClosed);
                window.removeEventListener('message', handleMessage);
            }
        }, 500);
    };

    const passwordResetCompletion = () => navigate("/");

    // --- Panel switch handlers ---

    const handleSwitchToLogin = () => {
        setShowRegisterPassword(false);
        setShowConfirmPassword(false);
        setLoginActive(!loginActive);
        switchToSignIn();
    };

    const handleSwitchToRegister = () => {
        setShowLoginPassword(false);
        setLoginActive(!loginActive);
        switchToSignUp();
    };

    const handleMobileSwitchToLogin = () => {
        setLoginErrorMessage("");
        setRegisterErrorMessage("");
        setLoginActive(!loginActive);
        switchToSignIn();
    };

    const handleMobileSwitchToRegister = () => {
        setLoginErrorMessage("");
        setRegisterErrorMessage("");
        setLoginActive(!loginActive);
        switchToSignUp();
    };

    return {
        // Form state
        name, setName,
        loginEmail, setLoginEmail,
        registerEmail, setRegisterEmail,
        loginPassword, setLoginPassword,
        registerPassword, setRegisterPassword,
        confirmPassword, setConfirmPassword,
        loginErrorMessage,
        registerErrorMessage,
        loginActive,

        // Async request state
        isLoggingIn,
        isSubmittingRegistration,

        // Bot traps
        loginHoneypot, setLoginHoneypot,
        registerHoneypot, setRegisterHoneypot,

        // Password visibility
        showLoginPassword,
        showRegisterPassword,
        showConfirmPassword,
        toggleLoginPasswordVisibility,
        toggleRegisterPasswordVisibility,
        toggleConfirmPasswordVisibility,

        // Modals
        showResetPasswordModal, setShowResetPasswordModal,
        showEmailOTPVerificationModal, setShowEmailOTPVerificationModal,

        // Auth status
        loggedInStatus,

        // Actions
        handleLogin,
        validateRegistrationForm,
        handleGoogleAuth,
        passwordResetCompletion,

        // Panel switches
        handleSwitchToLogin,
        handleSwitchToRegister,
        handleMobileSwitchToLogin,
        handleMobileSwitchToRegister,
    };
};
